import React, { useState } from "react";
import type { ECharts } from "echarts";
import { Download } from "lucide-react";
import { ButtonGroup, Dropdown } from "react-bootstrap";
import {
  downloadChartAsPdf,
  downloadChartAsSvg,
  downloadChartsAsPdf,
  downloadChartsAsSvg,
  downloadElementAsPdf,
  downloadElementAsSvg,
  type TitledChart,
} from "./downloadChartImage";

type ChartExportFormat = "svg" | "pdf";

type SharedProps = {
  fileName?: string;
  disabled?: boolean;
  className?: string;
  buttonClassName?: string;
};

type Props = SharedProps & (
  | { getChart: () => ECharts | undefined; getElement?: undefined; getCharts?: undefined }
  | { getElement: () => HTMLElement | null | undefined; getChart?: undefined; getCharts?: undefined }
  | { getCharts: () => TitledChart[]; getChart?: undefined; getElement?: undefined }
);

const ChartExportMenu: React.FC<Props> = ({
  fileName = "chart",
  disabled = false,
  className,
  buttonClassName,
  getChart,
  getElement,
  getCharts,
}) => {
  const [exporting, setExporting] = useState<ChartExportFormat | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);

  const handleExport = async (format: ChartExportFormat): Promise<void> => {
    const charts = getCharts?.() ?? [];
    const chart = getChart?.();
    const element = getElement?.();
    if (charts.length === 0 && !chart && !element) {
      setExportError("Chart is not ready to export.");
      return;
    }

    setExportError(null);
    setExporting(format);
    try {
      if (charts.length > 0) {
        if (format === "svg") {
          await downloadChartsAsSvg(charts, fileName);
          return;
        }
        await downloadChartsAsPdf(charts, fileName);
        return;
      }
      if (element) {
        if (format === "svg") {
          await downloadElementAsSvg(element, fileName);
          return;
        }
        await downloadElementAsPdf(element, fileName);
        return;
      }

      if (!chart) {
        setExportError("Chart is not ready to export.");
        return;
      }
      if (format === "svg") {
        await downloadChartAsSvg(chart, fileName);
        return;
      }
      await downloadChartAsPdf(chart, fileName);
    } catch (error) {
      console.error("Chart export failed", { format, fileName, error });
      setExportError("Export failed. Please try again.");
    } finally {
      setExporting(null);
    }
  };

  return (
    <div className={className}>
      <Dropdown as={ButtonGroup} align="end">
        <Dropdown.Toggle
          variant={buttonClassName ? "primary" : "light-primary"}
          size="sm"
          className={`py-1 d-inline-flex align-items-center gap-1${buttonClassName ? ` ${buttonClassName}` : ""}`}
          disabled={disabled || exporting !== null}
        >
          <Download size={14} aria-hidden />
          {exporting ? "Exporting..." : "Export"}
        </Dropdown.Toggle>
        <Dropdown.Menu>
          <Dropdown.Item onClick={() => { void handleExport("svg"); }}>
            Export to SVG
          </Dropdown.Item>
          <Dropdown.Item onClick={() => { void handleExport("pdf"); }}>
            Export to PDF
          </Dropdown.Item>
        </Dropdown.Menu>
      </Dropdown>
      {exportError && (
        <p className="text-danger small mb-0 mt-2" role="alert">{exportError}</p>
      )}
    </div>
  );
};

export default ChartExportMenu;
